import { MutationHandlingServiceV2 } from '@akebono/core';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  LotIncrementViewsCountGQL,
  LotIncrementViewsCountMutation,
  LotSource,
  LotTypeEnum,
} from '../modules/graphql/service/graphql-auto-main-service';

export type LotViewedEvent = {
  lotId: string;
  lotType: LotTypeEnum;
  lotSource: LotSource;
};

@Injectable({ providedIn: 'root' })
export class LotViewsService implements OnDestroy {
  private readonly lotViewedBroadcastChannel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('lotViewedBroadcastChannel')
      : null;

  private readonly lotViewedSubject = new Subject<LotViewedEvent>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private mhs: MutationHandlingServiceV2,
    private incrementViewsCountGQL: LotIncrementViewsCountGQL,
  ) {
    if (this.lotViewedBroadcastChannel) {
      this.lotViewedBroadcastChannel.onmessage = (message) => {
        this.lotViewedSubject.next(message.data);
      };
    }
  }

  public increment(
    lotId: string,
    lotType: LotTypeEnum,
    lotSource: LotSource,
  ): Promise<LotIncrementViewsCountMutation> {
    if (this.lotViewedBroadcastChannel) {
      this.lotViewedBroadcastChannel.postMessage(<LotViewedEvent>{
        lotId,
        lotType,
        lotSource,
      });
    } else {
      this.lotViewedSubject.next({
        lotId,
        lotType,
        lotSource,
      });
    }

    return this.mhs
      .mutate(
        this.incrementViewsCountGQL,
        {
          input: {
            lotId,
            lotType,
            lotSource,
          },
        },
        {
          refetch: false,
          renderError: false,
          renderSuccess: false,
        },
      )
      .data.pipe(take(1), takeUntil(this.destroy$))
      .toPromise();
  }

  get lotViewed$(): Observable<LotViewedEvent> {
    return this.lotViewedSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.lotViewedSubject.complete();
    this.lotViewedBroadcastChannel?.close();
  }
}
